import { useEffect, useMemo, useState } from 'react';
import AppModal from '../components/AppModal';
import api from '../services/api';
import { esAdministrador } from '../utils/usuarioActual';

const TABS = [
  {
    key: 'Tipo de Recibo',
    label: 'Tipos de Recibo'
  },
  {
    key: 'Concepto',
    label: 'Conceptos'
  },
  {
    key: 'Forma de pago',
    label: 'Formas de Pago'
  },
  {
    key: 'Lugar de Expedición',
    label: 'Lugares de Expedición'
  },
  {
    key: 'Moneda',
    label: 'Monedas'
  }
];

function estadoInicial(tipo = 'Tipo de Recibo') {
  return {
    Id: null,
    Tipo: tipo,
    Valor: '',
    Activo: true,
    CatalogoPadreId: ''
  };
}

function CatalogosPage() {
  const usuarioEsAdministrador = esAdministrador();
  
  const [tabActiva, setTabActiva] = useState('Tipo de Recibo');
  const [catalogos, setCatalogos] = useState([]);
  const [form, setForm] = useState(estadoInicial('Tipo de Recibo'));
  const [mensaje, setMensaje] = useState('');
  const [errores, setErrores] = useState([]);
  const [editando, setEditando] = useState(false);
  const [catalogoEstatusModal, setCatalogoEstatusModal] = useState(null);

  const [filtros, setFiltros] = useState({
    texto: '',
    estatus: 'activos'
  });

  useEffect(() => {
    cargarCatalogos();
  }, []);

  useEffect(() => {
    cancelarEdicion(false);
  }, [tabActiva]);

  async function cargarCatalogos() {
    try {
      const res = await api.get('/catalogos?incluirInactivos=1');
      setCatalogos(res.data);
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;

    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    });
  }

  function handleFiltro(e) {
    const { name, value } = e.target;

    setFiltros({
      ...filtros,
      [name]: value
    });
  }

  function validar() {
    const pendientes = [];

    if (!form.Valor.trim()) {
      pendientes.push('Valor es obligatorio.');
    }

    if (tabActiva === 'Concepto' && !form.CatalogoPadreId) {
      pendientes.push('Selecciona el Tipo de Recibo relacionado.');
    }

    return pendientes;
  }

  async function guardar(e) {
    e.preventDefault();

    setMensaje('');
    setErrores([]);

    const pendientes = validar();

    if (pendientes.length > 0) {
      setErrores(pendientes);
      setMensaje('Revisa los campos antes de guardar.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const payload = {
      Tipo: tabActiva,
      Valor: form.Valor.trim(),
      CatalogoPadreId:
        tabActiva === 'Concepto'
          ? Number(form.CatalogoPadreId)
          : null
    };

    if (usuarioEsAdministrador) {
      payload.Activo = form.Activo;
    }

    try {
      if (editando) {
        await api.put(`/catalogos/${form.Id}`, payload);
        setMensaje('Catálogo actualizado correctamente.');
      } else {
        await api.post('/catalogos', payload);
        setMensaje('Catálogo creado correctamente.');
      }

      setForm(estadoInicial(tabActiva));
      setEditando(false);
      await cargarCatalogos();

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function editarCatalogo(item) {
    setForm({
      Id: item.Id,
      Tipo: item.Tipo || tabActiva,
      Valor: item.Valor || '',
      Activo: Boolean(item.Activo),
      CatalogoPadreId: item.CatalogoPadreId
        ? String(item.CatalogoPadreId)
        : ''
    });

    setEditando(true);
    setMensaje('');
    setErrores([]);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelarEdicion(limpiarMensaje = true) {
    setForm(estadoInicial(tabActiva));
    setEditando(false);
    setErrores([]);

    if (limpiarMensaje) {
      setMensaje('');
    }
  }

  function cambiarEstatus(item) {
    setCatalogoEstatusModal(item);
  }

  async function confirmarCambioEstatus() {
    const item = catalogoEstatusModal;

    if (!item) return;

    setCatalogoEstatusModal(null);

    try {
      await api.delete(`/catalogos/${item.Id}`);
      setMensaje(`Catálogo ${item.Activo ? 'desactivado' : 'activado'} correctamente.`);
      await cargarCatalogos();
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  const tiposRecibo = useMemo(() => {
    return catalogos
      .filter(item =>
        item.Tipo === 'Tipo de Recibo' &&
        item.Activo
      )
      .sort((a, b) =>
        String(a.Valor).localeCompare(String(b.Valor))
      );
  }, [catalogos]);

  function obtenerNombrePadre(catalogoPadreId) {
    const padre = catalogos.find(item =>
      Number(item.Id) === Number(catalogoPadreId)
    );

    return padre?.Valor || '-';
  }

  const catalogosFiltrados = useMemo(() => {
    const texto = filtros.texto.trim().toLowerCase();

    return catalogos.filter((item) => {
      if (item.Tipo !== tabActiva) return false;

      const coincideTexto =
        !texto ||
        String(item.Valor || '').toLowerCase().includes(texto) ||
        String(obtenerNombrePadre(item.CatalogoPadreId) || '')
          .toLowerCase()
          .includes(texto);

      const coincideEstatus =
        filtros.estatus === 'todos' ||
        (filtros.estatus === 'activos' && item.Activo) ||
        (filtros.estatus === 'inactivos' && !item.Activo);

      return coincideTexto && coincideEstatus;
    });
  }, [catalogos, filtros, tabActiva]);

  const tituloFormulario = editando
    ? `Editar ${TABS.find(t => t.key === tabActiva)?.label || 'Catálogo'}`
    : `Nuevo ${TABS.find(t => t.key === tabActiva)?.label || 'Catálogo'}`;

  return (
    <div className="page-container-normal">

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Catálogos</h1>

          <div className="text-muted">
            Administración de listas base del sistema
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        {TABS.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${
                tabActiva === tab.key ? 'active' : ''
              }`}
              onClick={() => setTabActiva(tab.key)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      {errores.length > 0 && (
        <div className="alert alert-warning">
          <div className="fw-bold mb-2">
            ⚠ Campos pendientes:
          </div>

          <ul className="mb-0">
            {errores.map((error, index) => (
              <li key={index}>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <form onSubmit={guardar}>
        <div className="card-box mb-3">

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              {tituloFormulario}
            </h5>

            <span className="badge bg-secondary">
              {catalogosFiltrados.length} registros
            </span>
          </div>

          <div className="row g-3 align-items-end">

            {tabActiva === 'Concepto' && (
              <div className="col-md-4">
                <label className="form-label">
                  Tipo de Recibo relacionado <span className="text-danger">*</span>
                </label>

                <select
                  className="form-select"
                  name="CatalogoPadreId"
                  value={form.CatalogoPadreId}
                  onChange={handleChange}
                >
                  <option value="">Seleccione</option>

                  {tiposRecibo.map((tipo) => (
                    <option key={tipo.Id} value={tipo.Id}>
                      {tipo.Valor}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={tabActiva === 'Concepto' ? 'col-md-5' : 'col-md-7'}>
              <label className="form-label">
                Valor <span className="text-danger">*</span>
              </label>

              <input
                className="form-control"
                name="Valor"
                value={form.Valor}
                onChange={handleChange}
                placeholder={
                  tabActiva === 'Tipo de Recibo'
                    ? 'Ej. Gastos'
                    : tabActiva === 'Concepto'
                    ? 'Ej. Combustible'
                    : tabActiva === 'Lugar de Expedición'
                    ? 'Ej. Monterrey, N.L.'
                    : 'Captura valor'
                }
              />
            </div>

            {usuarioEsAdministrador && (
              <div className="col-md-1 d-flex align-items-end">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="Activo"
                    checked={form.Activo}
                    onChange={handleChange}
                    id="ActivoCatalogo"
                  />

                  <label className="form-check-label" htmlFor="ActivoCatalogo">
                    Activo
                  </label>
                </div>
              </div>
            )}

            <div className="col-md-2 d-flex align-items-end justify-content-end gap-2">
              <button
                type="submit"
                className="btn btn-primary px-4"
              >
                {editando ? 'Guardar' : 'Crear'}
              </button>

              {editando && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => cancelarEdicion()}
                >
                  Cancelar
                </button>
              )}
            </div>

          </div>
        </div>
      </form>

      <div className="card-box mb-3">
        <div className="row g-2 align-items-end">

          <div className="col-md-6">
            <label className="form-label">Buscar</label>

            <input
              className="form-control"
              name="texto"
              value={filtros.texto}
              onChange={handleFiltro}
              placeholder={
                tabActiva === 'Concepto'
                  ? 'Buscar concepto o tipo relacionado'
                  : 'Buscar valor'
              }
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Estatus</label>

            <select
              className="form-select"
              name="estatus"
              value={filtros.estatus}
              onChange={handleFiltro}
            >
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="todos">Todos</option>
            </select>
          </div>

          <div className="col-md-3 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary px-4"
              onClick={() =>
                setFiltros({
                  texto: '',
                  estatus: 'activos'
                })
              }
            >
              Limpiar
            </button>
          </div>

        </div>
      </div>

      <div className="card-box">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">
            {TABS.find(t => t.key === tabActiva)?.label}
          </h5>

          <span className="badge bg-secondary">
            {catalogosFiltrados.length} registros
          </span>
        </div>

        <div className="table-wrap">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                {tabActiva === 'Concepto' && (
                  <th>Tipo de Recibo</th>
                )}

                <th>Valor</th>
                <th className="text-center">Estatus</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {catalogosFiltrados.map((item) => (
                <tr key={item.Id}>
                  {tabActiva === 'Concepto' && (
                    <td>
                      {obtenerNombrePadre(item.CatalogoPadreId)}
                    </td>
                  )}

                  <td>
                    <strong>{item.Valor}</strong>
                  </td>

                  <td className="text-center">
                    {item.Activo ? (
                      <span className="badge bg-success">Activo</span>
                    ) : (
                      <span className="badge bg-secondary">Inactivo</span>
                    )}
                  </td>

                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => editarCatalogo(item)}
                      >
                        Editar
                      </button>

                      {usuarioEsAdministrador && (
                        <button
                          type="button"
                          className={
                            item.Activo
                              ? 'btn btn-outline-danger'
                              : 'btn btn-outline-success'
                          }
                          onClick={() => cambiarEstatus(item)}
                        >
                          {item.Activo ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {catalogosFiltrados.length === 0 && (
                <tr>
                  <td
                    colSpan={tabActiva === 'Concepto' ? 4 : 3}
                    className="text-center text-muted py-4"
                  >
                    No hay registros para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AppModal
        abierto={Boolean(catalogoEstatusModal)}
        titulo="Sistema Recibos"
        mensaje={
          catalogoEstatusModal
            ? `¿Deseas ${catalogoEstatusModal.Activo ? 'desactivar' : 'activar'} "${catalogoEstatusModal.Valor}"?`
            : ''
        }
        variante={catalogoEstatusModal?.Activo ? 'danger' : 'warning'}
        textoCancelar="Cancelar"
        textoAceptar={catalogoEstatusModal?.Activo ? 'Desactivar' : 'Activar'}
        onCancelar={() => setCatalogoEstatusModal(null)}
        onAceptar={confirmarCambioEstatus}
      />

    </div>
  );
}

export default CatalogosPage;
